const parseJson = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch (err) {
    return null;
  }
};

const IMPERSONATION_STACK_KEY = 'impersonator_stack';
const IMPERSONATOR_TOKEN_KEY = 'impersonator_token';
const IMPERSONATOR_USER_KEY = 'impersonator_user';

const hasStorage = () => typeof window !== 'undefined' && window.localStorage;

export const AUTH_SESSION_CHANGED_EVENT = 'credyou-auth-session-changed';

const decodeJwtPayload = (token) => {
  try {
    const payload = token?.split('.')[1];
    if (!payload || typeof window === 'undefined') return null;

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(window.atob(padded));
  } catch (err) {
    return null;
  }
};

const normalizeUser = (user) => {
  if (!user?.role) return null;

  return {
    id: user.id ?? null,
    name: user.name || '',
    mobile: user.mobile || '',
    email: user.email || '',
    username: user.username || '',
    role: user.role,
    profile_pic: user.profile_pic || '',
    admin_profile_pic: user.admin_profile_pic || '',
    admin_name: user.admin_name || '',
    admin_mobile: user.admin_mobile || '',
    brand_name: user.brand_name || '',
    brand_logo_url: user.brand_logo_url || '',
    brand_portal_url: user.brand_portal_url || '',
    brand_primary_color: user.brand_primary_color || '',
    brand_secondary_color: user.brand_secondary_color || '',
    brand_context_active: user.brand_context_active || 0,
    brand_owner_profile_pic: user.brand_owner_profile_pic || '',
    admin_id: user.admin_id ?? null,
    customer_id: user.customer_id ?? null,
    dealer_id: user.dealer_id ?? null,
    impersonated_by_id: user.impersonated_by_id ?? null,
    impersonated_by_name: user.impersonated_by_name || '',
    impersonated_by_role: user.impersonated_by_role || '',
    impersonated_by_mobile: user.impersonated_by_mobile || '',
  };
};

const mergeUserDetails = (primary, fallback) => {
  if (!primary) return fallback;
  if (!fallback) return primary;

  return {
    ...fallback,
    ...primary,
    id: primary.id ?? fallback.id,
    name: primary.name || fallback.name,
    mobile: primary.mobile || fallback.mobile,
    email: primary.email || fallback.email,
    username: primary.username || fallback.username,
    profile_pic: primary.profile_pic || fallback.profile_pic,
    admin_profile_pic: primary.admin_profile_pic || fallback.admin_profile_pic,
    admin_name: primary.admin_name || fallback.admin_name,
    admin_mobile: primary.admin_mobile || fallback.admin_mobile,
    brand_name: primary.brand_name || fallback.brand_name,
    brand_logo_url: primary.brand_logo_url || fallback.brand_logo_url,
    brand_portal_url: primary.brand_portal_url || fallback.brand_portal_url,
    brand_primary_color: primary.brand_primary_color || fallback.brand_primary_color,
    brand_secondary_color: primary.brand_secondary_color || fallback.brand_secondary_color,
    brand_context_active: primary.brand_context_active ?? fallback.brand_context_active,
    brand_owner_profile_pic: primary.brand_owner_profile_pic || fallback.brand_owner_profile_pic,
    admin_id: primary.admin_id ?? fallback.admin_id,
    customer_id: primary.customer_id ?? fallback.customer_id,
    dealer_id: primary.dealer_id ?? fallback.dealer_id,
    impersonated_by_id: primary.impersonated_by_id ?? fallback.impersonated_by_id,
    impersonated_by_name: primary.impersonated_by_name || fallback.impersonated_by_name,
    impersonated_by_role: primary.impersonated_by_role || fallback.impersonated_by_role,
    impersonated_by_mobile: primary.impersonated_by_mobile || fallback.impersonated_by_mobile,
  };
};

export const getCurrentUser = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const tokenPayload = decodeJwtPayload(token);
  if (tokenPayload?.exp && tokenPayload.exp * 1000 <= Date.now()) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATOR_USER_KEY);
    localStorage.removeItem(IMPERSONATION_STACK_KEY);
    return null;
  }

  const storedUser = normalizeUser(parseJson(localStorage.getItem('user')));
  const tokenUser = normalizeUser(tokenPayload);
  const sessionUser = mergeUserDetails(storedUser, tokenUser);

  if (sessionUser) {
    localStorage.setItem('user', JSON.stringify(sessionUser));
    return sessionUser;
  }

  localStorage.removeItem('token');
  localStorage.removeItem('user');
  return null;
};

export const setAuthSession = ({ token, user }) => {
  if (token) localStorage.setItem('token', token);

  const sessionUser = mergeUserDetails(normalizeUser(user), normalizeUser(decodeJwtPayload(token)));
  if (sessionUser) {
    localStorage.setItem('user', JSON.stringify(sessionUser));
  }

  return sessionUser;
};

export const getImpersonationStack = () => {
  if (!hasStorage()) return [];

  const stack = parseJson(localStorage.getItem(IMPERSONATION_STACK_KEY));
  if (Array.isArray(stack)) {
    return stack.filter((item) => item?.token && item?.user);
  }

  const token = localStorage.getItem(IMPERSONATOR_TOKEN_KEY);
  const user = localStorage.getItem(IMPERSONATOR_USER_KEY);
  return token && user ? [{ token, user }] : [];
};

export const getImpersonationReturnUser = () => {
  const stack = getImpersonationStack();
  const lastSession = stack[stack.length - 1];
  return normalizeUser(parseJson(lastSession?.user));
};

export const pushImpersonationSession = ({ token, user }) => {
  if (!hasStorage() || !token || !user) return;

  const stack = getImpersonationStack();
  const storedUser = typeof user === 'string' ? user : JSON.stringify(user);
  stack.push({ token, user: storedUser });
  localStorage.setItem(IMPERSONATION_STACK_KEY, JSON.stringify(stack));

  const firstSession = stack[0];
  localStorage.setItem(IMPERSONATOR_TOKEN_KEY, firstSession.token);
  localStorage.setItem(IMPERSONATOR_USER_KEY, firstSession.user);
};

export const popImpersonationSession = () => {
  if (!hasStorage()) return null;

  const stack = getImpersonationStack();
  const session = stack.pop() || null;

  if (stack.length > 0) {
    localStorage.setItem(IMPERSONATION_STACK_KEY, JSON.stringify(stack));
    const firstSession = stack[0];
    localStorage.setItem(IMPERSONATOR_TOKEN_KEY, firstSession.token);
    localStorage.setItem(IMPERSONATOR_USER_KEY, firstSession.user);
  } else {
    localStorage.removeItem(IMPERSONATION_STACK_KEY);
    localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATOR_USER_KEY);
  }

  return session;
};

export const clearAuthSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATOR_USER_KEY);
  localStorage.removeItem(IMPERSONATION_STACK_KEY);
};
